import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'preact/hooks'
import { api } from '../api/client'
import type { AnnouncementReport } from '../api/generated/AnnouncementReport'
import type { NewAnnouncement } from '../api/generated/NewAnnouncement'
import { useAction } from '../api/useAction'
import { formatDay, volunteers } from './format'

/** Same limit as the server, which sends the whole text by SMS when the push is not received. */
const MAX_BODY_CHARS = 500

const messages = {
  invalid_announcement: 'Annonce vide, trop longue, ou sans bénévole à prévenir.',
  rate_limited: '5 annonces par heure au plus : réessayez plus tard.',
}

/**
 * Writes an announcement to the accepted volunteers, of every day or of one of `days`; asks for
 * confirmation with the number of recipients before sending.
 */
export function AnnouncementDialog({
  slug,
  days,
  onSent,
  onClose,
}: {
  slug: string
  days: string[]
  onSent: () => void
  onClose: () => void
}) {
  const [body, setBody] = useState('')
  const [day, setDay] = useState('')
  const [recipients, setRecipients] = useState<number>()
  const { error, pending, run } = useAction(messages)
  const post = (dry_run: boolean) =>
    api.post<AnnouncementReport>(`/events/${slug}/announcements`, {
      body,
      day: day || null,
      dry_run,
    } satisfies NewAnnouncement)
  const submit = (e: Event) => {
    e.preventDefault()
    run(async () => {
      if (recipients === undefined) setRecipients((await post(true)).recipients)
      else {
        await post(false)
        onSent()
      }
    })
  }
  const edit = () => setRecipients(undefined)

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <form onSubmit={submit}>
        <DialogTitle>Nouvelle annonce</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Annonce"
              required
              autoFocus
              multiline
              minRows={3}
              value={body}
              disabled={recipients !== undefined}
              onInput={(e) => setBody((e.target as HTMLTextAreaElement).value)}
              helperText={`${body.length} / ${MAX_BODY_CHARS} caractères, envoyée par notification, sinon par SMS ou email`}
              slotProps={{ htmlInput: { maxLength: MAX_BODY_CHARS } }}
            />
            <TextField
              select
              label="Destinataires"
              value={day}
              // "" stands for all volunteers: MUI hides an empty value unless told otherwise
              slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
              disabled={recipients !== undefined}
              onChange={(e) => setDay((e.target as HTMLInputElement).value)}
            >
              <MenuItem value="">Tous les bénévoles validés</MenuItem>
              {days.map((d) => (
                <MenuItem key={d} value={d}>
                  Bénévoles validés du {formatDay(d)}
                </MenuItem>
              ))}
            </TextField>
            {recipients === 0 && (
              <Alert severity="warning">Aucun bénévole validé à prévenir.</Alert>
            )}
            {recipients !== undefined && recipients > 0 && (
              <Typography>
                Envoyer cette annonce à <strong>{volunteers(recipients)}</strong> ?
              </Typography>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          {recipients === undefined ? (
            <Button onClick={onClose}>Annuler</Button>
          ) : (
            <Button onClick={edit}>Modifier</Button>
          )}
          <Button
            type="submit"
            variant="contained"
            loading={pending}
            disabled={body.trim() === '' || recipients === 0}
          >
            {recipients === undefined ? 'Continuer' : 'Envoyer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
